import type { CSSProperties } from 'react';
import { Checkbox, Menu } from 'antd';

import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { useTeamGroups } from '@/hooks/useTeamGroups';
import type { TeamGroup } from '@/apis/teamGroups';
import { isNoTeamId } from '@/common/utils/noTeamFilter';
import {
  groupMatchesSearch,
  groupSelectionState,
  teamPickerSectionLabels,
  toggleGroupSelection,
} from '@/common/utils/teamGroups';
import type { ListItem, SecondaryFilterOption } from '../../ControlTower/types';

type TeamGroupOptionsProps = {
  /** Every team the picker offers, before any search narrows the list. */
  teamOptions: SecondaryFilterOption[];
  selectedOptions: SecondaryFilterOption[];
  setSelectedOptions: (options: ListItem[]) => void;
  searchQuery?: string;
  rowStyle: CSSProperties;
};

const teamLabel = (option: SecondaryFilterOption) =>
  option.title || option.name || String(option.id);

/**
 * Team filter only: team groups above the teams. Picking a group selects its
 * teams in this picker (picking it again clears them), so the selection,
 * and the filter value built from it, only ever holds team team ids.
 */
const TeamGroupOptions: React.FC<TeamGroupOptionsProps> = ({
  teamOptions,
  selectedOptions,
  setSelectedOptions,
  searchQuery = '',
  rowStyle,
}) => {
  // Teams must be teams a user can pick here: not "No teams", not disabled.
  const pickable = teamOptions.filter(
    (option) =>
      !isNoTeamId(option.id) &&
      option.enabled !== false &&
      option.disabled !== true
  );
  const groups = useTeamGroups(pickable.map((option) => String(option.id)));

  const nameById = new Map(
    pickable.map((option) => [String(option.id), teamLabel(option)])
  );
  const visibleGroups = groups.filter((group) =>
    groupMatchesSearch(group, searchQuery, (id) => nameById.get(id))
  );
  if (visibleGroups.length === 0) return null;

  const selectedIds = selectedOptions.map((option) => String(option.id));
  const sectionLabels = teamPickerSectionLabels();

  const handleToggle = (group: TeamGroup) => {
    const nextIds = new Set(toggleGroupSelection(group, selectedIds));
    const kept: ListItem[] = selectedOptions
      .filter((option) => nextIds.has(String(option.id)))
      .map((option) => ({ ...option, id: String(option.id) }));
    const keptIds = new Set(kept.map((option) => option.id));
    const added: ListItem[] = group.team_ids
      .filter((id) => nextIds.has(id) && !keptIds.has(id))
      .map((id) => ({ id, name: nameById.get(id) ?? id }));
    setSelectedOptions([...kept, ...added]);
  };

  return (
    <>
      <Menu.Item key='__team_groups_title' disabled style={rowStyle}>
        <span className='subfilter-team-groups-title'>
          {sectionLabels.groups}
        </span>
      </Menu.Item>
      {visibleGroups.map((group) => {
        const state = groupSelectionState(group, selectedIds);
        return (
          <Menu.Item key={`__team_group_${group.id}`} style={rowStyle}>
            <Checkbox
              className='custom-menu-shipment-checkbox-only checkbox-sm'
              checked={state.checked}
              indeterminate={state.indeterminate}
              onChange={() => handleToggle(group)}
            >
              <TruncateWithTooltip
                text={group.name}
                max={25}
                // The count stays visible when a long name is truncated.
                render={(t: string) => (
                  <span className='subfilter-option-label'>
                    {`${t} (${group.team_ids.length})`}
                  </span>
                )}
              />
            </Checkbox>
          </Menu.Item>
        );
      })}
      {/* Closes the group section so the teams below don't read as groups. */}
      <Menu.Item key='__teams_title' disabled style={rowStyle}>
        <span className='subfilter-team-groups-title'>
          {sectionLabels.teams}
        </span>
      </Menu.Item>
    </>
  );
};

export default TeamGroupOptions;
